import React, { useState } from "react";
import styles from "./Credit.module.scss";
import { useMyPanel } from "../../../hooks/useMyPanel";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import { strings } from "../../../intl/fa";
import { ArrowLeft, CloseIcon, Time } from "@bale/ui";
import classNames from "classnames";
import { useTheme } from "@emotion/react";
import { PackageItem } from "components/content/packagesPage/PackageItem";
import {
  createPackageInfoObject,
  packagesTitle,
} from "components/content/packagesPage/packages";
import FullpageModal from "components/commons/modal/fullpageModal/FullpageModal";
import { getLocaleDateTimeWithMonth, isISOTimeAfterToday } from "utils/time";
import ReactDOM from "react-dom";
import PackagesModal from "components/content/packagesPage/PackagesModal";
import { CampaignPackageType } from "core/orderAd/orderAd.entity";
import { useAppSelector } from "utils/store";
import { campaignDefaultPackageSelector } from "core/orderAd/orderAd.selector";

export const Credit: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [showPackageInfo, setShowPackageInfo] = useState(false);
  const theme = useTheme();
  const myPanel = useMyPanel();
  const hourTime = 3600 * 1000;
  const remainingTime =
    new Date(myPanel.credit_expire_time).getTime() - Date.now();
  let expireTimeLabel = "";
  if (remainingTime > 24 * hourTime) {
    expireTimeLabel =
      digitsToHindi(String(Math.ceil(remainingTime / (24 * hourTime)))) +
      " " +
      strings.credit.remaining_days;
  } else {
    expireTimeLabel =
      digitsToHindi(String(Math.ceil(remainingTime / hourTime))) +
      " " +
      strings.credit.remaining_hours;
  }

  const viewPackageInfo = () => {
    if (isCustomPack) return;
    setShowPackageInfo(true);
  };

  const selectedPackage = useAppSelector(
    campaignDefaultPackageSelector(myPanel.campaign_package_type)
  );

  const packageHasTime = isISOTimeAfterToday(myPanel.credit_expire_time);
  const isWaitingForActivation = isISOTimeAfterToday(myPanel.credit_start_date);
  const hasPackage =
    myPanel.campaign_package_type !== CampaignPackageType.NOPACKAGE;
  const isCustomPack =
    myPanel.campaign_package_type === CampaignPackageType.CUSTOM;
  const isPackageActive = packageHasTime && !isWaitingForActivation;
  const needsNewPackage = !(myPanel.credit > 0) || !packageHasTime;

  const bgIconPath = packageHasTime
    ? "/icons/darkCreditBg.svg"
    : "/icons/lightCreditBg.svg";

  return (
    <div className={styles.Container}>
      <div className={styles.TitleWrapper}>
        <p className={styles.Title}>{strings.credit.your_credit}</p>
        {isPackageActive && (
          <p className={styles.ExpireDate}>{expireTimeLabel}</p>
        )}
      </div>
      <div className={styles.Border}>
        <div
          className={classNames(styles.Card, {
            [styles.NoPackageCard]: !isPackageActive,
          })}
        >
          {packageHasTime ? (
            <>
              <p
                className={classNames(styles.CreditTitle, {
                  [styles.NoPackageText]: !isPackageActive,
                })}
              >
                {strings.credit.remaining}
              </p>
              <p
                className={classNames(styles.CreditAmount, {
                  [styles.NoPackageText]: !isPackageActive,
                })}
              >
                {digitsToHindi(toMoneyFormat(String(myPanel.credit))) +
                  " " +
                  strings.common.rial}
              </p>
            </>
          ) : (
            <div className={styles.CreditFinished}>
              {strings.credit.credit_expired}
            </div>
          )}
          <img src={bgIconPath} alt="" className={styles.IconBg1} />
          <img src={bgIconPath} alt="" className={styles.IconBg2} />
        </div>
        {hasPackage && isWaitingForActivation && (
          <div className={styles.ActivationTimeContainer}>
            <Time size={18} />
            <p className={styles.ActivationTime}>
              {strings.credit.activation_date.replace(
                "{date}",
                getLocaleDateTimeWithMonth(
                  new Date(myPanel.credit_start_date).getTime()
                )
              )}
            </p>
          </div>
        )}
      </div>

      <div className={styles.PackageDetailContainer} onClick={viewPackageInfo}>
        <div className={styles.ShowDetail}>
          <div className={styles.PackageInfo}>
            {needsNewPackage ? (
              <p className={styles.NeedPackage}>
                {strings.packages.sidebar.need_credit}
              </p>
            ) : (
              <>
                <p className={styles.YourPackage}>
                  {strings.packages.sidebar.your_package}
                </p>
                <p className={styles.PackageName}>
                  {
                    packagesTitle[
                      myPanel.campaign_package_type as CampaignPackageType
                    ]
                  }
                </p>
              </>
            )}
          </div>
          {!isCustomPack && (
            <div className={styles.ViewWrapper}>
              <p className={styles.View}>
                {needsNewPackage
                  ? strings.packages.sidebar.buy_credit
                  : strings.packages.sidebar.view}
              </p>
              <ArrowLeft size={18} color={theme.colors.primary[50]} />
            </div>
          )}
        </div>
      </div>
      {showPackageInfo &&
        !needsNewPackage &&
        ReactDOM.createPortal(
          <FullpageModal>
            <div className={styles.CloseIcon}>
              <CloseIcon size={32} onClick={() => setShowPackageInfo(false)} />
            </div>
            <p className={styles.FullModalTitle}>
              {strings.packages.sidebar.your_package}
            </p>
            <p className={styles.FullModalDesc}>
              {strings.packages.modal.buy_new_pack_desc}
            </p>
            {selectedPackage && (
              <PackageItem
                package={createPackageInfoObject(
                  selectedPackage,
                  myPanel.campaign_package_type
                )}
              />
            )}
          </FullpageModal>,
          document.getElementById("fullscreen-modal")!
        )}
      {showPackageInfo &&
        needsNewPackage &&
        ReactDOM.createPortal(
          <PackagesModal onClose={() => setShowPackageInfo(false)} />,
          document.getElementById("fullscreen-modal")!
        )}
    </div>
  );
};
